import { Trans } from '@lingui/react'
import classNames from 'clsx'
import { bool, func, string } from 'prop-types'
import { useContext } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { SA_SELLER_ID } from 'shared/consts/checkout'
import {
  getSelectedShippingAddress,
  hasClientBillingAddress,
  hasOrderCoolingRequired,
  hasOrderFreightTransport,
  isPupSelected as isPupSelectedReduxSelector,
} from 'shared/store-checkout/duck/address'
import { showModalDeclarativeAction } from 'shared/store/ducks/events'
import noop from 'shared/utils/noop'
import IconCaretRight from 'views/components/atoms/Icon/IconCaretRight'
import ButtonAsLink from 'views/components/molecules/ButtonAsLink'
import ModalShowButton from 'views/components/molecules/ModalShowButton'
import AddressFormContext from 'views/components/organisms/AddressForm/AddressFormContext'
import AddressModal from 'views/components/organisms/AddressModal'
import AddressSelectionModal from 'views/components/organisms/Checkout/AddressSelectionModal'
import { BILLING_FORM_ID } from 'views/consts'
import useMountPoints from 'views/enhancers/useMountPoints'
import './ShippingAddressOptionsList.scss'

const ShippingAddressOptionsList = ({
  hasMarketPlaceOrders,
  sellerId,
  subOrderId,
  showShipToMultipleOption,
}) => {
  const mountPoints = useMountPoints()
  const dispatch = useDispatch()
  const hasBillingAddress = useSelector(state => hasClientBillingAddress(state))
  const selectedShippingAddress = useSelector(state =>
    getSelectedShippingAddress(state, subOrderId)
  )
  const isFreightTransport = useSelector(state =>
    hasOrderFreightTransport(state, subOrderId)
  )
  const isPupSelected = useSelector(state =>
    isPupSelectedReduxSelector(state, subOrderId)
  )

  const shipmentBySubOrder = sellerId !== SA_SELLER_ID
  const containerClassName = classNames('o-ShippingAddress', {
    'a-box a-box--dark a-box--medium': !hasMarketPlaceOrders,
  })
  const listItemClassName = classNames('a-list__item', {
    'a-list-bordered__item': !hasMarketPlaceOrders,
  })

  const newAddressModalId = `new-shipping-address-${subOrderId}`
  const addressSelectionModalId = `select-shipping-address-${subOrderId}`

  const newAddressModalProps = {
    modalId: newAddressModalId,
    size: 'medium',
    title: <Trans id="modal.address.title.addAddress" />,
    csrfToken: 'csrf-test-token',
    type: 'shipping',
  }
  const addressSelectionModalProps = {
    sellerId,
    subOrderId,
    modalId: addressSelectionModalId,
    size: 'medium',
    title: <Trans id="checkout.shipping.SelectShippingAddress" />,
    csrfToken: 'csrf-test-token',
    type: 'shipping',
  }

  const { setPostBillingSubmitAction, setPostShippingSubmitAction } =
    useContext(AddressFormContext)

  const history = useHistory()

  const handleAddNewShippingAddress = () => {
    if (!hasBillingAddress && setPostBillingSubmitAction) {
      setPostBillingSubmitAction(() => () => {
        // PostSubmitAction Logic for adding a new shipping address
        dispatch(showModalDeclarativeAction(newAddressModalId))
        if (setPostShippingSubmitAction) {
          setPostShippingSubmitAction(() => () => {
            history.push(mountPoints.getCheckoutShipping())
          })
        }
      })
    } else {
      dispatch(showModalDeclarativeAction(newAddressModalId))
    }
  }

  const handleAddNewPickupStation = () => {
    if (!hasBillingAddress) {
      setPostBillingSubmitAction(() => () => {
        // PostSubmitAction Logic for adding a new pickup station//
      })
    } else {
      // PostSubmitAction Logic for adding a new pickup station
    }
  }
  const handleShipToMultipleAddresses = () => {
    if (!hasBillingAddress) {
      setPostBillingSubmitAction(() => () => {
        // PostSubmitAction Logic for shipping to multiple addresses
      })
    } else {
      // PostSubmitAction Logic for shipping to multiple addresses
    }
  }

  const isCoolingRequired = useSelector(state =>
    hasOrderCoolingRequired(state, subOrderId)
  )

  return (
    <>
      <div className={containerClassName}>
        {!shipmentBySubOrder && !hasMarketPlaceOrders && (
          <h3 className="o-ShippingAddressOptionsList__headline">
            <Trans id="addressStep.shippingAddressOptions.moreOptions" />
          </h3>
        )}
        <ul className="a-list-reset a-list-bordered">
          {hasMarketPlaceOrders && (
            <li className="a-list-item u-margin-large--bottom">
              <ModalShowButton
                name="shippingAddressInput"
                className="o-ShippingAddress__defaultAddress"
                reverse
                modalId={addressSelectionModalId}
              >
                <div className="o-ShippingAddress__defaultAddressTextWrapper">
                  <span className="o-ShippingAddress__defaultAddressTitle">
                    <Trans id="checkout.ShippingAddressOptionsList.shippingAddress" />
                  </span>
                  <span className="o-ShippingAddress__defaultAddressText u-margin-large--right">
                    {selectedShippingAddress}
                  </span>
                </div>
                <IconCaretRight className="o-ShippingAddress__defaultAddressIcon" />
              </ModalShowButton>
            </li>
          )}
          <li className={listItemClassName}>
            <ButtonAsLink
              name={newAddressModalProps.modalId}
              type="submit"
              form={BILLING_FORM_ID}
              className="o-ShippingAddress__button"
              fullWidth
              left
              onClick={handleAddNewShippingAddress}
            >
              <Trans id="checkout.ShippingAddressOptionsList.addNewAddress" />
            </ButtonAsLink>
          </li>
          {!shipmentBySubOrder && !isCoolingRequired && (
            <li className={listItemClassName}>
              <ButtonAsLink
                name="shippingAddressOption"
                type="submit"
                form={BILLING_FORM_ID}
                className="o-ShippingAddress__button"
                fullWidth
                left
                onClick={handleAddNewPickupStation}
              >
                <Trans id="checkout.ShippingAddressOptionsList.addNewPickupStation" />
              </ButtonAsLink>
            </li>
          )}
          {shipmentBySubOrder && !isPupSelected && (
            <li className={`${listItemClassName} o-ShippingAddress__label`}>
              {isFreightTransport && (
                <span>
                  <Trans id="checkout.ShippingAddressOptionsList.pupNotForFreightProducts" />
                </span>
              )}
              {!isFreightTransport && (
                <span>
                  <Trans id="checkout.ShippingAddressOptionsList.pupNotPossible" />
                </span>
              )}
            </li>
          )}
          {showShipToMultipleOption && (
            <li className={listItemClassName}>
              <ButtonAsLink
                name="shippingAddressOption"
                type="submit"
                form={BILLING_FORM_ID}
                className="o-ShippingAddress__button"
                fullWidth
                left
                onClick={handleShipToMultipleAddresses}
              >
                <Trans id="checkout.ShippingAddressOptionsList.shipToMultipleAddresses" />
              </ButtonAsLink>
            </li>
          )}
        </ul>
      </div>
      <AddressModal {...newAddressModalProps} />
      {hasMarketPlaceOrders && (
        <AddressSelectionModal {...addressSelectionModalProps} />
      )}
    </>
  )
}

ShippingAddressOptionsList.propTypes = {
  hasMarketPlaceOrders: bool,
  sellerId: string,
  subOrderId: string,
  showShipToMultipleOption: bool,
  showModal: func,
}

ShippingAddressOptionsList.defaultProps = {
  hasMarketPlaceOrders: false,
  sellerId: SA_SELLER_ID,
  subOrderId: null,
  showShipToMultipleOption: false,
  showModal: noop,
}

export default ShippingAddressOptionsList
